import { useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X, Sparkles, Play, BookOpen, Lightbulb, CreditCard, Mail } from "lucide-react";
import GoogleButton from "./GoogleButton";
import { openContact } from "./ContactModal";

export const goToAnalyzer = () => {
  const el = document.querySelector('[data-testid="script-input"]');
  if (!el) {
    window.location.href = "/#analizar";
    return;
  }
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  setTimeout(() => el.focus({ preventScroll: true }), 500);
};

const LINKS = [
  { id: "analizar", label: "Analizar video", icon: Play, action: goToAnalyzer },
  { id: "ejemplos", label: "Ejemplos", icon: BookOpen, href: "/ejemplos" },
  { id: "como-funciona", label: "Cómo funciona", icon: Lightbulb, href: "/#como-funciona" },
  { id: "precios", label: "Precios", icon: CreditCard, href: "/precios" },
  { id: "contacto", label: "Contacto", icon: Mail, action: openContact },
];

const linkCls = "vt-nav-link whitespace-nowrap text-sm font-semibold text-gray-600 hover:text-purple-700 transition-colors";

export function DesktopNav() {
  return (
    <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-4 xl:gap-6 shrink-0">
      {LINKS.map((l) =>
        l.href ? (
          <a key={l.id} href={l.href} data-testid={`nav-${l.id}`} className={linkCls}>{l.label}</a>
        ) : (
          <button key={l.id} type="button" onClick={l.action} data-testid={`nav-${l.id}`} className={linkCls}>{l.label}</button>
        )
      )}
    </nav>
  );
}

export function HeaderCTA() {
  return (
    <button type="button" onClick={goToAnalyzer} data-testid="header-analyze-cta" className="vt-btn-primary vt-cta-glow hidden md:inline-flex text-sm px-4 py-2 rounded-full">
      <Sparkles className="w-4 h-4" /> Analizar mi video
    </button>
  );
}

export function MobileMenu({ user }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="lg:hidden">
      <button type="button" onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Abrir menú" className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">
        <Menu className="w-5 h-5" />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[60]" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-[#0A1024] border-l border-white/10 shadow-2xl p-5 flex flex-col gap-2 vt-dark-zone">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold uppercase tracking-widest text-purple-300">Menú</span>
              <button type="button" onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Cerrar menú" className="p-2 rounded-lg text-white/80 hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </div>
            {LINKS.map((l) => {
              const Icon = l.icon;
              const inner = (<><span className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-400/30 text-purple-300 flex items-center justify-center"><Icon className="w-4 h-4" /></span>{l.label}</>);
              const cls = "flex items-center gap-3 rounded-xl px-3 py-3 text-base font-semibold text-white hover:bg-white/5 transition-colors";
              return l.href ? (
                <a key={l.id} href={l.href} data-testid={`mobile-nav-${l.id}`} className={cls} onClick={() => setOpen(false)}>{inner}</a>
              ) : (
                <button key={l.id} type="button" data-testid={`mobile-nav-${l.id}`} className={cls} onClick={() => { setOpen(false); setTimeout(l.action, 150); }}>{inner}</button>
              );
            })}
            <div className="mt-auto pt-4 border-t border-white/10 space-y-3">
              {user ? (
                <a href="/cuenta" data-testid="mobile-nav-cuenta" className="vt-btn-secondary w-full">Mi cuenta</a>
              ) : (
                <div data-testid="mobile-nav-login"><GoogleButton label="Iniciar sesión con Google" /></div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function MobileStickyCTA({ visible }) {
  if (!visible) return null;
  return (
    <div data-testid="mobile-sticky-cta" className="md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pt-2 pointer-events-none" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}>
      <div className="absolute inset-0 mobile-cta-fade" />
      <button type="button" onClick={goToAnalyzer} data-testid="mobile-sticky-cta-button" className="relative pointer-events-auto vt-btn-primary vt-cta-glow w-full py-3.5 text-base rounded-2xl">
        <Sparkles className="w-5 h-5" /> Analizar mi video
      </button>
    </div>
  );
}
